import { useAtomValue } from "jotai";
import { Module } from "../../types/TenantInterfaces";
import {
  CustomViewConfig,
  CustomViewToolbar,
} from "../../views/cloud/AIModels/AIModels";
import { Search } from "../PageView/PageViewTable/SharedToolbar/SharedToolbar";
import { IsMobileAtom } from "../../App";
import FiltersButton from "../SavedFilters/FiltersButton";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import SortBy from "../PageView/PageViewTable/SortBy";
import PageViewCards from "../PageView/PageViewTable/PageViewCards/PageViewCards";
import useQueriedRecords from "../../api/api-hooks/records/useQueriedRecords";
import { Query } from "../../types/GlobalInterfaces";

interface AppCustomViewProps {
  toolbarConfig: CustomViewToolbar;
  viewConfig: CustomViewConfig;
  moduleSchema: Module;
  CustomCardFooter?: ({
    record,
  }: {
    record: Record<string, any>;
  }) => React.ReactNode;
}

// For Hardcoded Views within the Application, not designed to be customized by the user.
// Was first created for the AI Models View in the Cloud Settings, which allows you to show/hide AI Models.
export default function AppCustomView({
  toolbarConfig,
  viewConfig,
  moduleSchema,
  CustomCardFooter,
}: AppCustomViewProps) {
  const isMobile = useAtomValue(IsMobileAtom);
  const [query, setQuery] = useState<Query>(() => viewConfig?.Query || {});
  const [, setSimpleSearchValue] = useState("");
  const [simpleSearchQuery, setSimpleSearchQuery] = useState<any>(null);

  // Sync the query with the viewConfig when it changes
  useEffect(() => {
    if (viewConfig?.Query) {
      setQuery(viewConfig.Query);
    }
  }, [viewConfig?.Query]);

  return (
    <>
      <div className="tw:flex tw:mt-2 tw:gap-2 tw:mb-4">
        {toolbarConfig.Search.Enabled && (
          <Search
            isMobile={isMobile}
            clearSimpleSearch={() => setSimpleSearchValue("")}
            currentModule={moduleSchema}
            setSimpleSearchQuery={setSimpleSearchQuery}
            setSimpleSearchValue={setSimpleSearchValue}
            query={viewConfig}
          />
        )}
        {toolbarConfig.Filters.Enabled && (
          <FiltersButton
            currentModule={moduleSchema}
            query={query}
            setQuery={setQuery}
            clearFilter={() => setQuery((prev) => ({ ...prev, Filters: [] }))}
          />
        )}
        {toolbarConfig.Sort.Enabled && (
          <SortBy
            savedSortBy={viewConfig?.Query?.OrderBy || undefined}
            sortBy={query?.OrderBy || undefined}
            updateSortBy={(sortBy: any) =>
              setQuery((prev) => ({ ...prev, OrderBy: sortBy }))
            }
            moduleAPIName={moduleSchema?.APIName}
          />
        )}
      </div>
      <AppCustomViewRenderer
        viewConfig={viewConfig}
        moduleSchema={moduleSchema}
        query={simpleSearchQuery || query}
        setQuery={setQuery}
        CustomCardFooter={CustomCardFooter}
      />
    </>
  );
}

function AppCustomViewRenderer({
  viewConfig,
  moduleSchema,
  query,
  setQuery,
  CustomCardFooter,
}: {
  viewConfig: CustomViewConfig;
  moduleSchema: Module;
  query: Query;
  setQuery: Dispatch<SetStateAction<Query>>;
  CustomCardFooter?: ({
    record,
  }: {
    record: Record<string, any>;
  }) => React.ReactNode;
}) {
  const { records, recordsLoading } = useQueriedRecords({
    moduleAPIName: moduleSchema?.APIName,
    query: query,
  });

  if (viewConfig?.Type === "Cards") {
    return (
      <PageViewCards
        currentModule={moduleSchema}
        viewMode="Cards"
        records={records}
        recordsLoading={recordsLoading}
        query={query}
        setQuery={setQuery}
        // @ts-ignore
        view={viewConfig}
        foundLayout={viewConfig?.CardsLayouts?.[0]}
        commentsWidget={false}
        CustomCardFooter={CustomCardFooter}
      />
    );
  }
}
